export interface AnchoredListing {
  id: string;
  title?: string;
  url?: string;
  photos?: string[];
  price_thb?: string | number;
  usd_price?: string | number;
  price_quote?: string | number;
  quote_currency?: string;
  district?: string;
  area_sqm?: string | number;
  property_type?: string;
}

export type MessageSegment<T extends AnchoredListing> =
  | { kind: 'text'; text: string }
  | { kind: 'listing'; listing: T }
  | { kind: 'image'; url: string };

const imageLine = /^https?:\/\/\S+\.(?:jpg|jpeg|png|webp|gif)(?:\?\S*)?$/i;

function isPriceLine(line: string) {
  return line.length <= 80 && !line.includes('http') && /(?:THB|USD|RUB|EUR)/i.test(line);
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Strip common Markdown decorations so plain chat stays readable if the model slips. */
export function plainChatText(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|\s)\*([^*\n]+)\*(?=\s|$)/g, '$1$2')
    .replace(/(^|\s)_([^_\n]+)_(?=\s|$)/g, '$1$2')
    .replace(/^#{1,6}\s+/gm, '');
}

function stripListingAnchor(line: string, id: string): string {
  return plainChatText(
    line
      .replace(new RegExp(`id\\s*[=:]?\\s*${escapeRegExp(id)}`, 'i'), '')
      .replace(new RegExp(escapeRegExp(id), 'g'), '')
      .replace(/[()[\]]/g, '')
      .replace(/^[\s\u2014\u2013\-–—:,.]+/, '')
      .replace(/[\s\u2014\u2013\-–—:,.]+$/, '')
      .trim(),
  );
}

function lineIsImage(line: string) {
  return imageLine.test(line.trim());
}

function listingOnLine<T extends AnchoredListing>(line: string, listings: T[]): T | undefined {
  const trimmed = line.trim();
  return listings.find((listing) => {
    if (listing.url && (trimmed === listing.url || line.includes(listing.url))) return true;
    if (!listing.id) return false;
    return (
      trimmed === listing.id ||
      line.includes(`id=${listing.id}`) ||
      line.includes(`id:${listing.id}`)
    );
  });
}

/**
 * A stored reply is plain text plus lot URLs (and sometimes a bare image URL).
 * Each lot URL, with the title and price lines directly above it, becomes a
 * card. Older replies that still contain `id=<id>` do the same.
 */
export function splitMessageByListings<T extends AnchoredListing>(
  content: string,
  listings: T[] | undefined,
): MessageSegment<T>[] {
  const onlyListings = (listings ?? []).filter((listing) => Boolean(listing?.id || listing?.url));
  if (onlyListings.length === 0 && !content.split('\n').some(lineIsImage)) {
    const text = plainChatText(content);
    return text.trim() ? [{ kind: 'text', text }] : [];
  }

  const pending = [...onlyListings];
  const segments: MessageSegment<T>[] = [];
  let buffer: string[] = [];

  const flush = () => {
    const text = plainChatText(buffer.join('\n')).replace(/^\n+|\n+$/g, '');
    if (text.trim()) segments.push({ kind: 'text', text });
    buffer = [];
  };

  const take = (listing: T) => {
    const index = pending.indexOf(listing);
    if (index >= 0) pending.splice(index, 1);
  };

  for (const line of content.split('\n')) {
    if (lineIsImage(line)) {
      flush();
      segments.push({ kind: 'image', url: line.trim() });
      continue;
    }
    const matched = listingOnLine(line, pending);
    if (!matched) {
      buffer.push(line);
      continue;
    }
    take(matched);
    const last = buffer[buffer.length - 1]?.trim() ?? '';
    if (isPriceLine(last)) buffer.pop();
    const title = matched.title?.trim();
    if (title && buffer[buffer.length - 1]?.trim() === title) buffer.pop();
    const residual = matched.id ? stripListingAnchor(line, matched.id) : line.trim();
    const withoutUrl = matched.url
      ? residual.replace(matched.url, '').replace(/^[\s\u2014\u2013\-–—:,.]+|[\s\u2014\u2013\-–—:,.]+$/g, '').trim()
      : residual;
    if (withoutUrl.trim() && withoutUrl.trim() !== matched.url) buffer.push(withoutUrl);
    flush();
    segments.push({ kind: 'listing', listing: matched });
  }
  flush();

  for (const listing of pending) {
    segments.push({ kind: 'listing', listing });
  }
  return segments;
}
